import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { toCSS } from '../../helpers/styles';
import useInterval from '../../hooks/useInterval';
import useResource from '../../hooks/useResource';
import MonitorContext from '../S80/MonitorContext';
import { ANIMATED_ELEMENT_TYPES, AnimatedElement } from './AnimatedPoint';
import HookablePoint from './HookablePoint';

export interface ITracksOnImageProps {
  point: any;
  resource: string;
  monitor: string;
  trackNumber: string;
  hookProperties: any;
  movement: any;
  hookSize?: number;
}

interface Position {
  x: number;
  y: number;
  width: number;
  height: number;
  duration: number;
}

const StyledImage = styled.image<any>`
  cursor: pointer;
  ${({ x, y, width, height, duration }) => `
        x: ${toCSS(+x - +width / 2)};
        y: ${toCSS(+y - +height / 2)};
        transition: x ${duration}ms ease, y ${duration}ms ease;
    `};
`;
const Container = styled.svg`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  & > image {
    position: absolute;
  }
`;

const StyledText = styled.text<any>`
  ${({ transitionTime, posX, posY }) => `
        transform: translate(${toCSS(+posX)},${toCSS(+posY)});
        transition: transform ${transitionTime}ms ease;
    `};
`;

export default function TracksOnImage() {
  const tracks = useSelector((state: any) => state.executor.tracksOnImage);
  const { monitor } = useContext<any>(MonitorContext);
  const activeMonitor = useSelector(
    (state: any) => state?.executor?.s80?.touchPanelVariables?.activeMonitor
  );

  if (monitor !== activeMonitor) {
    return <div data-area="tracks-on-image"></div>;
  }

  return (
    <Container data-area="tracks-on-image">
      {tracks.map((track: any, index: number) => {
        const {
          symbol,
          x,
          y,
          trackNumber,
          hookProperties,
          movement,
          hookSize,
        } = track;
        return (
          <TrackOnImage
            key={`${symbol}${index}`}
            resource={symbol}
            point={{
              x,
              y,
            }}
            monitor={monitor}
            trackNumber={trackNumber}
            hookProperties={hookProperties}
            movement={movement}
            hookSize={hookSize}
          />
        );
      })}
    </Container>
  );
}

function TrackOnImage(props: ITracksOnImageProps) {
  const {
    point,
    resource,
    monitor,
    trackNumber,
    hookProperties,
    movement,
    hookSize,
  } = props;

  const [transitionTime, setTransitionTime] = useState<any>(null);
  const [destinationPoint, setDestinationPoint] = useState({
    x: 0,
    y: 0,
  });

  const [trackPos, setTrackPos] = useState({
    x: 0,
    y: 0,
  });

  const url = useResource(resource);

  useEffect(() => {
    setTrackPos({
      x: point.x,
      y: point.y,
    });
  }, [point]);

  useEffect(() => {
    if (movement) {
      const { direction, distance } = movement;
      setTransitionTime(1000);
      let newDestPos = { x: 0, y: 0 };
      switch (direction) {
        case 'up':
          newDestPos = {
            x: point.x,
            y: point.y - distance,
          };
          break;
        case 'down':
          newDestPos = {
            x: point.x,
            y: point.y + +distance,
          };
          break;
        case 'left':
          newDestPos = {
            x: point.x - distance,
            y: point.y,
          };
          break;
        case 'right':
          newDestPos = {
            x: point.x + +distance,
            y: point.y,
          };
      }
      setDestinationPoint(newDestPos);
    } else {
      setTransitionTime(null);
    }
  }, [movement]);

  useInterval(() => {
    if (movement) {
      const { direction, speed, distance } = movement;
      let newPos = { x: 0, y: 0 };
      const pxPerTick = +speed;
      switch (direction) {
        case 'up':
          newPos = {
            x: trackPos.x,
            y: trackPos.y - pxPerTick,
          };
          break;
        case 'down':
          newPos = {
            x: trackPos.x,
            y: trackPos.y + pxPerTick,
          };
          break;
        case 'left':
          newPos = {
            x: trackPos.x - pxPerTick,
            y: trackPos.y,
          };
          break;
        case 'right':
          newPos = {
            x: trackPos.x + pxPerTick,
            y: trackPos.y,
          };
          break;
        default:
          newPos = trackPos;
      }
      /*
       * Improvement: There is a possibility that the track will overshoot
       * and this will cause the track to perpetually move in one direction.
       * We should instead check for overshooting and set the position to the
       * destination point if the track overshoots.
       */
      if (newPos.x === destinationPoint.x && newPos.y === destinationPoint.y) {
        setTrackPos({
          x: newPos.x,
          y: newPos.y,
        });
        setTransitionTime(null);
      } else {
        setTrackPos({
          x: newPos.x,
          y: newPos.y,
        });
      }
    }
  }, transitionTime);

  return (
    <>
      <HookablePoint
        x={trackPos.x}
        transitionTime={transitionTime}
        y={trackPos.y}
        id={trackNumber}
        imageUrl={url}
        hookProperties={hookProperties}
        hookSize={hookSize}
      />
      <AnimatedElement
        strokeWidth="0.5px"
        x={0}
        y={0}
        duration={transitionTime}
        elementType={ANIMATED_ELEMENT_TYPES.TEXT}
        transitionObject={{
          transform: `translate(${toCSS((point.x + 10).toFixed(2))}, ${toCSS(
            (point.y - 2).toFixed(2)
          )})`,
        }}
        textAnchor={'middle'}
        pointName={trackNumber}
        fontSize={'0.5em'}
        fill="yellow"
        fontWeight="normal"
      />
      {/* <StyledText
        posX={point.x + 10}
        posY={point.y - 2}
        x="0"
        y="0"
        transitionTime={transitionTime}
        text-anchor="middle"
        fill="yellow"
        strokeWidth="0.5px"
        fontSize="0.5em"
        fontWeight="normal"
      >
        {trackNumber?.toUpperCase()}
      </StyledText>*/}
    </>
  );
}
